<template>
  <v-container>
    <validation-observer>
      <validation-provider ref="provider" v-slot="{ errors }" :name="$t('product.productSearch')" :rules="computedRules">
        <v-text-field
          v-model="searchQuery"
          :label="$t('product.productSearch')"
          :outlined="outlined"
          clearable
          dense
          :error-messages="errors"
          :hide-details="errors.length === 0"
          :loading="loadingProducts"
          @click:clear="clearSearch"
          @keydown.enter.prevent="onSearchEnter"
        />
        <v-col v-if="selectedVariants.length">
          {{ $t('components.catalogSearch.activeFilters') }} :
          <v-chip
            v-for="(variant, index) in selectedVariants"
            :key="index"
            close
            :class="`mr-1 mb-1 text-wrap font-${mobile ? '12' : '16'}`"
            @click:close="removeVariant(index)"
          >
            {{ buildFilterLabel(variant) }}
          </v-chip>
        </v-col>
        <div
          v-if="canAddSearchQuery"
          class="mb-2 text-center"
          :class="{
            'mt-2': selectedVariants.length === 0,
          }"
        >
          <v-btn text color="primary" class="outlined" @click="addArbitraryValue">
            <v-icon left>mdi-plus-circle</v-icon>
            <span>{{ $t('components.catalogSearch.addValue', { value: searchQuery?.trim() }) }}</span>
          </v-btn>
        </div>
        <v-btn v-if="selectedProduct" class="pl-0" text @click="selectedProduct = null">
          <v-icon>mdi-chevron-left</v-icon>
          {{ $t('components.catalogSearch.back') }}
        </v-btn>
        <v-virtual-scroll
          v-if="displayItems.length"
          :items="displayItems"
          height="360"
          :item-height="itemHeight"
          bench="5"
        >
          <template v-slot:default="{ item }">
            <v-col>
              <v-row>
                <v-col cols="4" sm="2" md="2">
                  <v-img :src="getImageUrl(item)" :max-height="itemHeight - 8" class="d-flex align-center">
                    <template v-slot:placeholder>
                      <v-skeleton-loader type="image" />
                    </template>
                  </v-img>
                </v-col>
                <v-col cols="8" sm="10" md="10">
                  <v-row>
                    <v-col cols="12">
                      <v-list-item-title :class="`font-weight-black text-wrap font-${mobile ? '12' : '16'}`">
                        {{ item.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle :class="`font-${mobile ? '12' : '14'}`">
                        {{ $t('product.ref') }} : {{ item.id }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle v-if="item.options?.color?.value" :class="`font-${mobile ? '12' : '14'}`">
                        {{ $t('product.color') }} : {{ item.options.color.value }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle v-if="item.options?.size?.value" :class="`font-${mobile ? '12' : '14'}`">
                        {{ $t('product.size') }} : {{ item.options.size.value }}
                      </v-list-item-subtitle>
                      <v-btn
                        v-if="!mobile"
                        @click="!selectedProduct ? selectProduct(item) : selectVariant(item)"
                        color="primary"
                        depressed
                        :disabled="variantAlreadySelected(item)"
                        :small="mobile"
                      >
                        <span v-if="selectedProduct && !variantAlreadySelected(item)">
                          {{ $t('product.select') }}
                        </span>
                        <span v-else-if="selectedProduct && variantAlreadySelected(item)">
                          {{ $t('product.alreadySelected') }}
                        </span>
                        <span v-else>
                          {{ $t('product.details') }}
                        </span>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row class="d-flex justify-end align-center pa-1">
                <v-btn
                  v-if="mobile"
                  @click="!selectedProduct ? selectProduct(item) : selectVariant(item)"
                  color="primary"
                  depressed
                  :disabled="variantAlreadySelected(item)"
                  :small="mobile"
                >
                  <span v-if="selectedProduct && !variantAlreadySelected(item)">
                    {{ $t('product.select') }}
                  </span>
                  <span v-else-if="selectedProduct && variantAlreadySelected(item)">
                    {{ $t('product.alreadySelected') }}
                  </span>
                  <span v-else>
                    {{ $t('product.details') }}
                  </span>
                </v-btn>
              </v-row>
            </v-col>
          </template>
        </v-virtual-scroll>

        <div v-if="showNoResults" class="mt-4 text-center">
          {{ $t('components.catalogSearch.noResults') }}
        </div>
      </validation-provider>
    </validation-observer>
  </v-container>
</template>

<script>
  import _ from 'lodash'
  import { OctipasCatalogService } from '@/services/octipasCatalogService'
  import { mapState } from 'vuex'
  import { ValidationProvider, ValidationObserver } from 'vee-validate'
  import { textExists } from '../../helpers/helper'

  export default {
    name: 'OCatalogSearch',
    components: {
      ValidationProvider,
      ValidationObserver,
    },
    props: {
      outlined: {
        type: Boolean,
        default: true,
      },
      selectedItems: {
        type: Array,
        default: () => [],
      },
      // Minimum length before firing a catalog autocomplete request.
      // Guards against noisy/expensive searches on 1-2 character queries.
      minSearchLength: {
        type: Number,
        default: 3,
      },
      // Minimum length before allowing an arbitrary (unknown) reference to be added.
      // Decoupled from minSearchLength: an unknown ref can be shorter than a catalog search.
      minValueLength: {
        type: Number,
        default: 1,
      },
      // Extra vee-validate rules merged on top of the min rule derived from minValueLength.
      rules: {
        type: Object,
        default: () => ({}),
      },
      resetKey: {
        type: Number,
        default: 0,
      },
    },
    data() {
      return {
        searchQuery: '',
        products: [],
        variants: [],
        defaultImage: require('@/assets/img/products/product_default.png'),
        selectedProduct: null,
        selectedVariants: [],
        currentSearchQuery: '',
        loadingProducts: false,
        octipasCatalogService: null,
      }
    },
    created() {
      // Debounce on the instance (not in `methods`) so `.cancel()` stays available:
      // Vue 2 re-binds methods, which would strip lodash's cancel/flush helpers.
      this.getProducts = _.debounce(this.getProducts, 500)
    },
    mounted() {
      if (this.computedCatalogAccess) {
        this.octipasCatalogService = new OctipasCatalogService(this.partnerSettings.octipas_catalog_access, this.$auth)
      }
    },
    computed: {
      ...mapState(['partnerSettings']),
      computedRules() {
        return { min: this.minValueLength, ...this.rules }
      },
      displayItems() {
        return this.selectedProduct ? this.variants : this.products
      },
      showNoResults() {
        return (
          !this.loadingProducts &&
          this.searchQuery?.length >= this.minSearchLength &&
          !this.displayItems.length &&
          !this.selectedProduct
        )
      },
      itemHeight() {
        switch (this.$vuetify.breakpoint.name) {
          case 'xs':
            return 160
          case 'sm':
            return 140
          case 'md':
            return 160
          default:
            return 180
        }
      },
      mobile() {
        return this.$vuetify.breakpoint.xsOnly
      },
      computedCatalogAccess() {
        return this.partnerSettings.octipas_catalog_access?.enabled
      },
      canAddSearchQuery() {
        const trimmedValue = this.searchQuery?.trim() || ''
        return (
          !this.loadingProducts &&
          trimmedValue.length >= this.minValueLength &&
          !this.variantAlreadySelected({ id: trimmedValue })
        )
      },
    },
    watch: {
      searchQuery(newQuery) {
        const trimmedQuery = newQuery?.trim()
        if (!trimmedQuery || trimmedQuery.length < this.minSearchLength) {
          this.clearSearch()
          return
        }

        if (this.computedCatalogAccess) {
          this.loadingProducts = true
          this.selectedProduct = null
          this.getProducts(trimmedQuery)
        }
      },
      resetKey() {
        this.searchQuery = ''
        this.products = []
        this.selectedProduct = null
        this.selectedVariants = []
        this.$emit('input', [])
        this.$emit('update:emitSelectedItems', [])
      },
      selectedItems: {
        immediate: true,
        handler(newItems) {
          this.selectedVariants = [...newItems]
        },
      },
    },
    methods: {
      selectProduct(product) {
        this.selectedProduct = product
        this.getVariants(product)
      },
      selectVariant(variant) {
        const variantAlreadySelected = this.selectedVariants.some((item) => item.id === variant.id)
        if (!variantAlreadySelected) {
          this.selectedVariants = [...this.selectedVariants, variant]
          this.$emit(
            'input',
            this.selectedVariants.map((variant) => variant.id)
          )
          this.$emit('update:emitSelectedItems', this.selectedVariants)
        }
      },
      removeVariant(index) {
        if (index !== -1) {
          this.selectedVariants.splice(index, 1)
          this.$emit('update:emitSelectedItems', this.selectedVariants)
          this.$emit(
            'input',
            this.selectedVariants.map((variant) => variant.id)
          )
        }
      },
      clearSearch() {
        this.getProducts.cancel()
        this.products = []
        this.selectedProduct = null
        this.loadingProducts = false
      },
      getImageUrl(item) {
        const image = item?.image ?? item?.images
        if (textExists(image)) {
          return image
        }
        if (Array.isArray(image) && textExists(image?.[0]?.url)) {
          return image[0].url
        }
        return this.defaultImage
      },
      getProducts: async function (query) {
        this.currentSearchQuery = query
        this.loadingProducts = true
        this.products = []
        try {
          const res = await this.octipasCatalogService.autocompleteProducts(query, this.$i18n.locale)
          // check if search query has not changed in the meantime
          if (this.currentSearchQuery === this.searchQuery && res.length) {
            this.products = res
          }
        } catch (e) {
          console.log(e)
        } finally {
          this.loadingProducts = false
        }
      },
      getVariants: async function (product) {
        this.variants = []
        try {
          const res = await this.octipasCatalogService.getProductById(product.id, this.$i18n.locale)
          this.variants = res.variants
        } catch (e) {
          console.log(e)
        }
      },
      variantAlreadySelected(variant) {
        return this.selectedVariants.some((item) => item.id === variant.id)
      },
      onSearchEnter(event) {
        const trimmedValue = this.searchQuery?.trim()
        if (!trimmedValue) {
          return
        }
        event.stopPropagation()
        if (this.variantAlreadySelected({ id: trimmedValue })) {
          this.$refs.provider.setErrors([this.$t('components.catalogSearch.alreadyInList', { value: trimmedValue })])
          return
        }
        this.addArbitraryValue()
      },
      addArbitraryValue() {
        if (!this.canAddSearchQuery) {
          return
        }

        const trimmedValue = this.searchQuery.trim()
        const arbitraryItem = { id: trimmedValue, name: trimmedValue, isArbitrary: true }
        this.selectedVariants = [...this.selectedVariants, arbitraryItem]
        this.$emit(
          'input',
          this.selectedVariants.map((variant) => variant.id)
        )
        this.$emit('update:emitSelectedItems', this.selectedVariants)
        this.searchQuery = ''
        this.products = []
        this.selectedProduct = null
      },
      buildFilterLabel(variant) {
        if (variant.isArbitrary) {
          return variant.id
        }

        let label = variant.name

        if (variant.options?.color?.value) {
          label += ` - ${variant.options.color.value}`
        }

        if (variant.options?.size?.value) {
          label += ` - ${variant.options.size.value}`
        }

        label += ` - ${variant.id}`
        return label
      },
    },
  }
</script>
